import { cn } from "@comandero/shared";

interface LogoProps {
  size?: "sm" | "lg";
  class?: string;
}

/**
 * Marca propia de Comandero.
 *
 * The glyph is a cloche (service bell) on its plate, with
 * rising steam: the exact moment a dish goes out to the dining room. Evokes
 * "order fast, serve hot, control everything".
 *
 * Drawn with `currentColor` to inherit container color. ALWAYS accompanied
 * within a solid chip (`bg-accent` + `text-accent-foreground`) to
 * guarantee contrast in any theme, avoiding the gotcha of
 * `text-accent` on subtle surfaces (invisible in several themes).
 */
function ComanderoMark(props: { class?: string }) {
  return (
    <svg
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      class={props.class}
      aria-hidden="true"
    >
      {/* Vapor (servicio caliente) */}
      <path
        d="M12.5 6.5c1.2-1 1.2-2.2 0-3.2M16 6c1.2-1 1.2-2.2 0-3.2M19.5 6.5c1.2-1 1.2-2.2 0-3.2"
        stroke="currentColor"
        stroke-width="1.7"
        stroke-linecap="round"
        stroke-opacity="0.65"
      />
      {/* Domo sólido de la cloche */}
      <path
        d="M6 21a10 10 0 0 1 20 0Z"
        fill="currentColor"
        stroke="currentColor"
        stroke-width="2"
        stroke-linejoin="round"
      />
      {/* Tirador superior */}
      <circle cx="16" cy="9.4" r="1.9" fill="currentColor" />
      {/* Plato / bandeja de servicio */}
      <path d="M3.5 21.5h25" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" />
      <path
        d="M6.5 25h19"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-opacity="0.5"
      />
    </svg>
  );
}

export function Logo(props: LogoProps) {
  const size = () => props.size ?? "sm";

  return (
    <div
      class={cn(
        "flex items-center gap-3 select-none",
        size() === "lg" && "flex-col gap-4 text-center",
        props.class,
      )}
    >
      {size() === "sm" ? (
        <>
          <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground shadow-lg shadow-accent/25">
            <ComanderoMark class="h-5.5 w-5.5" />
          </div>
          <span class="text-xl font-bold tracking-tight text-foreground">
            Comand<span class="text-accent">ero</span>
          </span>
        </>
      ) : (
        <>
          <div class="mb-2 flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-accent text-accent-foreground shadow-xl shadow-accent/30 transition-transform duration-300 hover:scale-105 active:scale-95">
            <ComanderoMark class="h-9 w-9" />
          </div>
          <h1 class="text-3xl font-bold tracking-tight text-foreground">
            Comand<span class="text-accent">ero</span>
          </h1>
        </>
      )}
    </div>
  );
}
